import React, { useState, useEffect } from 'react';

interface LoadingProps {
  fullScreen?: boolean;
  text?: string;
  commands?: string[];
}

const Loading: React.FC<LoadingProps> = ({ 
  fullScreen = true, 
  text = '正在加载系统',
  commands = [
    'initializing system...',
    'connecting to server...',
    'fetching data...',
    'parsing response...',
    'rendering content...'
  ]
}) => {
  const [currentCommandIndex, setCurrentCommandIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [completedCommands, setCompletedCommands] = useState<string[]>([]);
  
  // Container classes based on fullScreen prop
  const containerClasses = fullScreen 
    ? 'flex flex-col items-start justify-center h-screen bg-black bg-opacity-90 backdrop-blur-sm p-8' 
    : 'flex flex-col items-start justify-center py-8 px-6 bg-black bg-opacity-80 rounded-md';
  
  // Terminal effect for typing commands
  useEffect(() => {
    if (currentCommandIndex >= commands.length) return;
    
    const command = commands[currentCommandIndex];
    
    if (currentCharIndex < command.length) {
      // Typing effect
      const typingSpeed = 30 + Math.random() * 70; // Random typing speed for realistic effect
      const timer = setTimeout(() => {
        setCurrentCharIndex(currentCharIndex + 1);
      }, typingSpeed);
      
      return () => clearTimeout(timer);
    } else {
      // Command completed, wait before moving to next command
      const timer = setTimeout(() => {
        setCompletedCommands(prev => [...prev, command]);
        setCurrentCommandIndex(currentCommandIndex + 1);
        setCurrentCharIndex(0);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [currentCommandIndex, currentCharIndex, commands]);
  
  // Blinking cursor effect
  useEffect(() => {
    const timer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    
    return () => clearInterval(timer);
  }, []);
  
  // Scan line effect
  const scanLineStyle = {
    backgroundImage: `repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0,255,255,0.03) 2px,
      rgba(0,255,255,0.03) 4px
    )`,
    pointerEvents: 'none' as const
  };
  
  return (
    <div className={containerClasses}>
      {/* Terminal header */}
      <div className="w-full max-w-2xl mx-auto mb-4 border-b border-cyan-800 pb-2 flex justify-between items-center">
        <div className="text-cyan-400 font-mono text-sm">{text}</div>
        <div className="flex space-x-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
      </div>
      
      {/* Terminal content */}
      <div className="w-full max-w-2xl mx-auto font-mono text-sm relative overflow-hidden rounded-md border border-cyan-900 bg-gray-900 p-4" style={{minHeight: '200px'}}>
        {/* Completed commands */}
        {completedCommands.map((cmd, index) => (
          <div key={index} className="mb-2">
            <span className="text-green-500">$ </span>
            <span className="text-cyan-400">{cmd}</span>
            <span className="text-green-300 ml-2">[OK]</span>
          </div>
        ))}
        
        {/* Current command being typed */}
        {currentCommandIndex < commands.length && (
          <div>
            <span className="text-green-500">$ </span>
            <span className="text-cyan-400">
              {commands[currentCommandIndex].substring(0, currentCharIndex)}
            </span>
            {showCursor && <span className="text-cyan-400 animate-pulse">_</span>}
          </div>
        )}
        
        {/* Command completed */}
        {currentCommandIndex >= commands.length && (
          <div className="mt-4">
            <span className="text-green-500">$ </span>
            <span className="text-cyan-400">system ready</span>
            {showCursor && <span className="text-cyan-400 animate-pulse">_</span>}
          </div>
        )}
        
        {/* Terminal scan line effect */}
        <div 
          className="absolute inset-0 opacity-30"
          style={scanLineStyle}
        />
      </div>
      
      {/* Progress bar */}
      <div className="w-full max-w-2xl mx-auto mt-4 bg-gray-800 rounded-full h-1.5 overflow-hidden">
        <div 
          className="bg-cyan-500 h-1.5 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${Math.min(100, (currentCommandIndex / commands.length) * 100)}%` }}
        />
      </div>
    </div>
  );
};

export default Loading;
